import { Children, cloneElement, ReactElement, ReactNode, useState } from 'react';
import cn from 'classnames';
import styles from './styles.scss';
import { IconArrow } from 'components/Shared/Icon';
import Overlay from 'components/Shared/Overlay';
import Button, { AppearanceTypes as ButtonAppearanceTypes } from 'components/Shared/Button';
import { format } from 'utils';
import { SelectInjectedProps, Option, OptionProps } from './option';
import { useToggle } from 'hooks';

export { Option };
type AppearanceTypes = 'primary' | 'secondary' | 'clear';

export interface SelectClassNames {
  input?: string;
  icon?: string;
  selected?: string;
  optionList?: string;
  appearance?: string;
}

export interface SelectProps {
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
  placeHolder?: string;
  firstSelected?: boolean;
  inverted?: boolean;
  selectedValue: number | string | undefined;
  onChange: (value: number | string) => void;
  appearance?: AppearanceTypes;
  scrollable?: boolean;
  classNames?: SelectClassNames;
  labelFormat?: string;
  isCustomButton?: boolean;
  children: ReactNode;
  valid?: boolean;
  invalid?: boolean;
}

type Direction = 'prev' | 'next';
interface GetFocusedOptionIndexArgs {
  direction: Direction;
  current?: number;
  max: number;
}

const directions: { [key: string]: Direction } = {
  ArrowDown: 'next',
  ArrowUp: 'prev'
};

const getFocusedOptionIndex = ({ direction, current, max }: GetFocusedOptionIndexArgs) => {
  if (current === undefined) {
    return direction === 'prev' ? max - 1 : 0;
  } else if (direction === 'next') {
    return current + 1 === max ? 0 : current + 1;
  } else if (direction === 'prev') {
    return current - 1 === -1 ? max - 1 : current - 1;
  }

  return 0;
};

export const Select = ({
  className,
  classNames = {},
  inverted,
  disabled,
  placeHolder,
  labelFormat,
  scrollable,
  selectedValue,
  onChange,
  children,
  appearance = 'primary',
  isCustomButton,
  valid,
  invalid
}: SelectProps) => {
  const [open, setOpen] = useToggle(false);
  const [focusedOption, setFocusedOption] = useState<number>();

  const onOpen = () => !disabled && setOpen(true);
  const onClose = () => {
    setOpen(false);
    setFocusedOption(undefined);
  };
  const handleSelect = (option: OptionProps) => () => {
    setOpen(false);
    onChange(option.value);
  };
  const clearFocusedOption = () => setFocusedOption(undefined);
  let selectedOption = placeHolder;

  const numberOfOptions = Children.toArray(children).filter(
    (child: ReactElement<OptionProps>) => child.type === Option
  ).length;

  const options = Children.map(children, (child: ReactElement<OptionProps>, i) => {
    if (child.type === Option) {
      const selected = child.props.value === selectedValue;
      if (selected) {
        selectedOption = labelFormat ? format(labelFormat, child.props.label) : child.props.label;
      }
      return cloneElement(child, {
        ...child.props,
        selected,
        labelFormat,
        className: cn(
          styles.option,
          child.props.className,
          {
            [styles.primary]: appearance === 'primary',
            [styles.secondary]: appearance === 'secondary',
            [styles.focused]: i === focusedOption
          },
          classNames.appearance
        ),
        onClick: handleSelect(child.props)
      } as SelectInjectedProps);
    } else {
      return child;
    }
  }) as ReactElement<OptionProps, string | React.JSXElementConstructor<Record<string, unknown>>>[];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const spacebar = 32;
    if (e.key === 'Tab') {
      onClose();
      return;
    }
    e.preventDefault();
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const next = getFocusedOptionIndex({
        current: focusedOption,
        max: options.length,
        direction: directions[e.key]
      });
      setFocusedOption(next);
      if (!open) {
        setOpen(true);
      }
    } else if (e.key === 'Escape') {
      clearFocusedOption();
      setOpen(false);
    } else if (e.keyCode === spacebar) {
      setOpen(true);
    } else if (e.key === 'Enter') {
      const option = options.find((_, i) => i === focusedOption);
      if (option !== undefined) {
        handleSelect(option.props)();
      }
    }
  };

  const input = (
    <Button
      appearance="reset"
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className={cn(
        styles.input,
        {
          [styles.open]: open,
          [styles.disabled]: disabled,
          [styles.customInput]: isCustomButton,
          [styles.valid]: valid,
          [styles.invalid]: invalid
        },
        classNames.input
      )}
      disabled={disabled}
    >
      <span className={cn(styles.selectedValue, classNames.selected)}>{selectedOption}</span>
      {appearance === 'primary' ? (
        <span
          className={cn(styles.icon, classNames.icon, {
            [styles.customArrowPadding]: isCustomButton
          })}
        >
          <IconArrow size="small" appearance="flip-90" />
        </span>
      ) : (
        <span className={cn(styles.iconWrapper, classNames.icon)} />
      )}
    </Button>
  );

  if (numberOfOptions === 1) {
    return (
      <div
        className={cn(
          styles.select,
          {
            [styles.inverted]: inverted,
            [styles.primary]: appearance === 'primary',
            [styles.secondary]: appearance === 'secondary'
          },
          styles.single,
          className
        )}
      >
        <div className={cn(styles.input, { [styles.disabled]: disabled }, classNames.input)}>
          <span className={cn(styles.selectedValue, classNames.selected)}>{selectedOption}</span>
        </div>
      </div>
    );
  }
  return (
    <Overlay transparent visible={open} onClick={onClose}>
      <div
        className={cn(
          styles.select,
          {
            [styles.inverted]: inverted,
            [styles.primary]: appearance === 'primary',
            [styles.secondary]: appearance === 'secondary'
          },
          className
        )}
      >
        {input}
        {!disabled && open && (
          <ul
            onMouseOver={clearFocusedOption}
            className={cn(
              {
                [styles.open]: open,
                [styles.scrollable]: scrollable,
                [styles.primary]: appearance === 'primary',
                [styles.secondary]: appearance === 'secondary'
              },
              classNames.optionList
            )}
          >
            {options}
          </ul>
        )}
      </div>
    </Overlay>
  );
};

interface ButtonSelectProps {
  options: OptionProps[];
  selectedValue: number;
  onChange: (value: number) => void;
  className?: string;
  appearance?: ButtonAppearanceTypes;
}

const handleOnButtonSelect =
  (onChange: (value: number | string) => void, value: number | string) => () =>
    onChange(value);

export const ButtonSelect = ({
  options,
  selectedValue,
  onChange,
  className,
  appearance
}: ButtonSelectProps) => (
  <div className={cn(styles.buttonSelect, className)}>
    {options.map(option => (
      <Button
        key={option.value}
        inverted={option.value !== selectedValue}
        onClick={handleOnButtonSelect(onChange, option.value)}
        appearance={appearance}
      >
        {option.label}
      </Button>
    ))}
  </div>
);
